/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {getCurrentUser, loadUsers, showUserProfileDialog} from '@/store/actions/dashboard';
import {userSelector} from '@/store/selectors/login-selectors';
import {checkOverflow} from '../utils/ComponentUtils';

export interface DesktopUserProfileViewType {
  onLogout: () => void;
  handleClick: Function;
  open: boolean;
  id: string;
  anchorEl: any;
  isLoggingOut: boolean;
  username: string;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : ({enableProfile}: {enableProfile: boolean}) => enableProfile ? dlsColor('grey1') : dlsColor('grey5'),
    fontWeight : 500
  },
  overflow : {
    textOverflow : 'ellipsis',
    overflow     : 'hidden'
  },
  options : {
    fontSize   : '14px',
    lineHeight : '20px',
    color      : dlsColor('grey2'),
    fontWeight : 400,
    cursor     : 'pointer'
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  },
  container : {
    width           : 260,
    backgroundColor : dlsColor('white')
  },
  userWrapper : {
    width        : '100%',
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  margin : {
    marginLeft  : 15,
    marginRight : 15,
    width       : '90%'
  },
  subContainer : {
    width      : '100%',
    alignItems : 'center',
    display    : 'flex',
    height     : 84
  },
  optionWrapper : {
    width          : '100%',
    height         : 48,
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between'
  }
}));

/**
 * @DesktopUserProfileView
 *
 * The Desktop user profile view  component provides the application user profile view
 * logout option and
 * ion preview.
 */
export const DesktopUserProfileView = ({
  handleClick,
  open,
  id,
  anchorEl,
  onLogout,
  isLoggingOut,
  username
}: DesktopUserProfileViewType) => {
  const user        = useSelector(userSelector);
  const accountName = user?.account?.title;
  const classes     = useStyles({enableProfile: true});
  const dispatch    = useDispatch();
  const userNameRef = useRef(null);
  const accNameRef  = useRef(null);
  const isMounted   = useHasMounted();

  const [overflowed, setIsOverflowed] = useState({userName: false, accountName: false});

  useLayoutEffect(() => {
    if (isMounted) {
      setIsOverflowed(previous => ({
        ...previous,
        userName    : checkOverflow(userNameRef.current),
        accountName : checkOverflow(accNameRef.current)
      }));
    }
  }, [isMounted]);

  function launchUserProfileDialog() {
    handleClick();
    dispatch(getCurrentUser(user));
    dispatch(loadUsers());
    dispatch(showUserProfileDialog(true));
  };

  return (
    <>
      <Popover anchorEl={anchorEl} id={id}
        style={{marginLeft: '-20px', zIndex: 3003}}
        open={open}
        anchorReference='anchorPosition'
        anchorPosition={{top: 90, left: (window.innerWidth)}}
        anchorOrigin={{
          vertical   : 'top',
          horizontal : 'left'
        }}
        transformOrigin={{
          vertical   : 'top',
          horizontal : 'left'
        }}>
        <ClickAwayListener onClickAway={function(e) {
          handleClick(e);
        }}>
          <div className={classes.container}>
            <div className={classes.userWrapper}>
              <div className={classes.margin}>
                <div className={classes.subContainer}>
                  <div>
                    <Avatar name={username} />
                  </div>
                  <div ref={accNameRef} className={classes.overflow} style={{marginLeft: '10px'}}>
                    <Tooltip title={username}
                      disableHoverListener={overflowed.userName}>
                      <label ref={userNameRef} className={classnames(classes.user, classes.overflow)}>{username}</label>
                    </Tooltip>
                    <Tooltip title={accountName}
                      disableHoverListener={overflowed.accountName}>
                      <label className={classnames(classes.account)}>{accountName}</label>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.optionWrapper} style={{borderTop: `1px solid ${dlsColor('grey5')}`}}>
              <CTALink style={{marginLeft: 20}}
                className={classes.options}
                onClick={launchUserProfileDialog}>
                {translate('nav:header.profile')}
              </CTALink>
            </div>
            <div className={classes.optionWrapper} style={{borderTop: `1px solid ${dlsColor('grey5')}`}}>
              <CTALink style={{pointerEvents: isLoggingOut ? 'none' : 'initial', marginLeft: 20}}
                className={classes.options}
                onClick={onLogout}>
                {translate('nav:header.logout')}
              </CTALink>
            </div>
          </div>
        </ClickAwayListener>
      </Popover>
    </>
  );
};

export default DesktopUserProfileView;
